import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/padroes";
import { StatusBadge } from "@/components/StatusBadge";
import { DemonstrativoCard } from "@/components/folha/DemonstrativoCard";
import { HoleriteCard } from "@/components/folha/HoleriteCard";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatCents, formatDate, formatMesRef } from "@/lib/format";
import { cn } from "@/lib/utils";
import { mesCurto } from "@/data/dashboard";
import {
  ROTULO_MODALIDADE,
  type Demonstrativo as Dados,
  type DemonstrativoHistorico,
  type Emprestimo,
  type Holerite,
  type HoleriteHistorico,
  ROTULO_TIPO_HOLERITE,
} from "@/data/folha";
import { toast } from "sonner";
import { FilePlus2, FileText, Loader2 } from "lucide-react";

/** Quantas competências aparecem no seletor em pílulas. */
const MAX_PILULAS = 6;

const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Seletor de mês em pílulas (a ativa fica escura). */
const PilulasMes = ({
  competencias,
  ativa,
  onChange,
}: {
  competencias: string[];
  ativa: string | undefined;
  onChange: (c: string) => void;
}) =>
  competencias.length > 1 ? (
    <div className="flex flex-wrap gap-1.5 print:hidden" role="radiogroup" aria-label="Competência">
      {competencias.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={c === ativa}
          onClick={() => onChange(c)}
          className={cn(
            "h-8 rounded-full border px-3 text-[13px] font-medium transition-colors",
            c === ativa ? "border-foreground bg-foreground text-background" : "bg-card hover:bg-row-hover",
          )}
        >
          {mesCurto(c)}
        </button>
      ))}
    </div>
  ) : null;

/** Card da coluna lateral: título de 14px e linhas separadas por borda. */
const CardLateral = ({ titulo, children }: { titulo: string; children: ReactNode }) => (
  <section className="flex flex-col rounded-xl border bg-card px-[18px] py-4">
    <h3 className="mb-2.5 text-sm font-semibold">{titulo}</h3>
    {children}
  </section>
);

/** Linha da coluna lateral; clicável quando recebe onClick. */
const LinhaLateral = ({
  titulo,
  apoio,
  valor,
  ativa,
  onClick,
}: {
  titulo: ReactNode;
  apoio?: ReactNode;
  valor?: ReactNode;
  ativa?: boolean;
  onClick?: () => void;
}) => {
  const conteudo = (
    <>
      <span className="flex min-w-0 flex-col gap-0.5 text-left">
        <span className="font-medium">{titulo}</span>
        {apoio && <span className="text-xs text-muted-foreground">{apoio}</span>}
      </span>
      {valor && <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">{valor}</span>}
    </>
  );
  const classes = "flex w-full justify-between gap-3 border-t border-border-soft py-2 text-[13px]";
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cn(classes, "-mx-2 w-[calc(100%+1rem)] rounded-md px-2 transition-colors hover:bg-row-hover", ativa && "bg-muted-soft")}
    >
      {conteudo}
    </button>
  ) : (
    <div className={classes}>{conteudo}</div>
  );
};

const Carregando = () => (
  <div className="flex justify-center py-20">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

const Vazio = ({ titulo, texto }: { titulo: string; texto: string }) => (
  <section className="rounded-xl border bg-card px-6 py-16 text-center">
    <FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
    <p className="font-medium">{titulo}</p>
    <p className="mt-1 text-sm text-muted-foreground">{texto}</p>
  </section>
);

/** CLT: holerite, recibo de férias e 13º — documentos da folha calculada, sem NF. */
const HoleriteColaborador = () => {
  const { competencia } = useParams();
  const [params] = useSearchParams();
  const doc = params.get("doc");
  const nav = useNavigate();
  const [holerite, setHolerite] = useState<Holerite | null>(null);
  const [historico, setHistorico] = useState<HoleriteHistorico[]>([]);
  const [carregando, setCarregando] = useState(true);

  const load = useCallback(async () => {
    setCarregando(true);
    try {
      const query = doc ? `?id=${encodeURIComponent(doc)}` : competencia ? `?competencia=${competencia}` : "";
      const [h, hist] = await Promise.all([
        api.get<Holerite | null>(`/folhas/me/holerite${query}`),
        api.get<HoleriteHistorico[]>("/folhas/me/holerites"),
      ]);
      setHolerite(h);
      setHistorico(hist);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar o holerite");
    } finally {
      setCarregando(false);
    }
  }, [competencia, doc]);

  useEffect(() => {
    void load();
  }, [load]);

  // Uma pílula por competência; férias e 13º do mesmo mês ficam em "Meus documentos".
  const competencias = [...new Set(historico.map((h) => h.competencia))].slice(0, MAX_PILULAS);
  const mensal = !holerite?.tipo || holerite.tipo === "mensal";

  return (
    <AppLayout title="Meu Holerite">
      <div className="mx-auto flex max-w-[1080px] flex-col gap-4 tabular-nums">
        <PageHeader
          titulo="Meu Holerite"
          subtitulo="Seus holerites e documentos da folha CLT."
          className="print:hidden"
          acoes={
            <PilulasMes
              competencias={competencias}
              ativa={holerite?.folha.competencia}
              onChange={(c) => nav(`/colaborador/demonstrativo/${c}`)}
            />
          }
        />

        {carregando ? (
          <Carregando />
        ) : !holerite ? (
          <Vazio
            titulo="Nenhum holerite disponível"
            texto="O holerite aparece quando o RH fecha a folha da competência."
          />
        ) : (
          <div className="flex flex-wrap items-start gap-4">
            <HoleriteCard holerite={holerite} imprimivel className="flex-[2_1_520px]" />

            <aside className="flex min-w-0 flex-[1_1_280px] flex-col gap-4 print:hidden">
              {historico.length > 0 && (
                <CardLateral titulo="Meus documentos">
                  {historico.map((h) => (
                    <LinhaLateral
                      key={h.id}
                      titulo={h.tipo === "mensal" ? "Holerite" : ROTULO_TIPO_HOLERITE[h.tipo]}
                      apoio={capitalizar(formatMesRef(h.competencia))}
                      valor={formatCents(h.liquido)}
                      ativa={h.id === holerite.id}
                      onClick={() => nav(`/colaborador/demonstrativo/${h.competencia}?doc=${h.id}`)}
                    />
                  ))}
                </CardLateral>
              )}

              {holerite.valorFgts > 0 && (
                <CardLateral titulo={mensal ? "FGTS do mês" : "FGTS"}>
                  <LinhaLateral
                    titulo={`Depositado em ${formatMesRef(holerite.folha.competencia).split(" ")[0]}`}
                    apoio={`Sobre a base de ${formatCents(holerite.baseFgts)}`}
                    valor={formatCents(holerite.valorFgts)}
                  />
                </CardLateral>
              )}
            </aside>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

const ColaboradorDemonstrativo = () => {
  const { user } = useAuth();
  return (
    <RoleGuard role={["colaborador", "rh", "financeiro"]}>
      {user?.vinculo === "clt" ? <HoleriteColaborador /> : <DemonstrativoPJ />}
    </RoleGuard>
  );
};

const DemonstrativoPJ = () => {
  const { competencia } = useParams();
  const nav = useNavigate();
  const [dados, setDados] = useState<Dados | null>(null);
  const [historico, setHistorico] = useState<DemonstrativoHistorico[]>([]);
  const [emprestimos, setEmprestimos] = useState<Emprestimo[]>([]);
  const [carregando, setCarregando] = useState(true);

  const load = useCallback(async () => {
    setCarregando(true);
    try {
      const query = competencia ? `?competencia=${competencia}` : "";
      const [d, h, e] = await Promise.all([
        api.get<Dados | null>(`/folhas/me${query}`),
        api.get<DemonstrativoHistorico[]>("/folhas/me/historico"),
        api.get<Emprestimo[]>("/emprestimos/me"),
      ]);
      setDados(d);
      setHistorico(h);
      setEmprestimos(e.filter((x) => x.status === "ativo"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar demonstrativo");
    } finally {
      setCarregando(false);
    }
  }, [competencia]);

  useEffect(() => {
    void load();
  }, [load]);

  const anteriores = historico.filter((h) => h.competencia !== dados?.competencia);

  return (
    <AppLayout title="Meu Demonstrativo">
      <div className="mx-auto flex max-w-[1080px] flex-col gap-4 tabular-nums">
        <PageHeader
          titulo="Meu Demonstrativo"
          subtitulo="A composição do valor de cada NF de salário."
          acoes={
            <PilulasMes
              competencias={historico.slice(0, MAX_PILULAS).map((h) => h.competencia)}
              ativa={dados?.competencia}
              onChange={(c) => nav(`/colaborador/demonstrativo/${c}`)}
            />
          }
        />

        {carregando ? (
          <Carregando />
        ) : !dados ? (
          <Vazio
            titulo="Nenhum demonstrativo disponível"
            texto="O demonstrativo aparece quando o RH fecha a folha da competência."
          />
        ) : (
          <div className="flex flex-wrap items-start gap-4">
            <DemonstrativoCard
              dados={dados}
              competencia={dados.competencia}
              franquiaAmil={dados.franquiaAmil}
              subtitulo={
                dados.tomadorNome
                  ? `Como chegamos ao valor da sua NF de salário · emitir para ${dados.tomadorNome}`
                  : "Como chegamos ao valor da sua NF de salário"
              }
              className="flex-[2_1_520px]"
            />

            <aside className="flex min-w-0 flex-[1_1_280px] flex-col gap-4">
              <CardLateral titulo="Nota fiscal desta competência">
                {dados.nf ? (
                  <div className="flex items-center justify-between gap-3 border-t border-border-soft py-2 text-[13px]">
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="font-medium">NF nº {dados.nf.numero}</span>
                      <span className="tabular-nums text-muted-foreground">{formatCents(dados.nf.valor * 100)}</span>
                    </span>
                    <StatusBadge status={dados.nf.status} />
                  </div>
                ) : (
                  <div className="flex flex-col gap-3 border-t border-border-soft pt-2.5">
                    <p className="text-[13px] text-muted-foreground">Você ainda não enviou a nota desta competência.</p>
                    <Button size="sm" className="w-fit" onClick={() => nav("/colaborador/nova")}>
                      <FilePlus2 /> Enviar NF
                    </Button>
                  </div>
                )}
              </CardLateral>

              <CardLateral titulo="Empréstimos e descontos em aberto">
                {emprestimos.length === 0 ? (
                  <LinhaLateral titulo="Nenhum lançamento ativo" apoio="Os descontos aparecem aqui quando houver" />
                ) : (
                  emprestimos.map((e) => (
                    <LinhaLateral
                      key={e.id}
                      titulo={ROTULO_MODALIDADE[e.modalidade]}
                      apoio={
                        <>
                          {formatCents(e.valorTotal)} contratado
                          {e.parcelas.length > 0 &&
                            ` · ${e.parcelas.filter((p) => p.status === "descontada").length} de ${e.parcelas.length} parcelas pagas`}
                        </>
                      }
                      valor={`${formatCents(e.saldo)} restante`}
                    />
                  ))
                )}
              </CardLateral>

              {anteriores.length > 0 && (
                <CardLateral titulo="Competências anteriores">
                  {anteriores.map((h) => (
                    <LinhaLateral
                      key={h.competencia}
                      titulo={capitalizar(formatMesRef(h.competencia))}
                      apoio={h.fechadaEm ? `Fechada em ${formatDate(h.fechadaEm)}` : undefined}
                      valor={formatCents(h.liquido)}
                      onClick={() => nav(`/colaborador/demonstrativo/${h.competencia}`)}
                    />
                  ))}
                </CardLateral>
              )}
            </aside>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ColaboradorDemonstrativo;
